import { crossEnvClient } from '../../../../api/crossEnvClient';
import { PermissionCode } from '../../../../auth/permissionCatalog';
import { EnvSnapshot, GroupSnapshot, RoleSnapshot, UserSnapshot } from './types';

const GROUP_FETCH_CONCURRENCY = 5;

/** Runs `fn` over `items` with at most `limit` in flight at once — used for
 * the per-user `GET .../groups` fan-out (one env can have hundreds of
 * users; unbounded parallelism would hammer the target). */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

interface RawAdminUser {
  user_id: number;
  email: string;
  display_name: string | null;
  active: boolean;
  role_v2_code?: string;
}

interface RawRole {
  role_code: string;
  role_name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
}

interface RawGroup {
  id: number;
  name: string;
  level: 'read' | 'write';
  members: { scope_type: 'tenant' | 'customer' | 'sponsor'; scope_code: string }[];
}

interface RawPermission {
  permission_code: string;
}

/** Fetches the full env-local access picture for `env` — roles + their
 * permissions, the permission catalog, all users + their resolved v2 role,
 * every tenant group + members, each user's group NAMES, and the
 * tenant/customer/sponsor scope-code universe (for group-member
 * validation). All reads, all browser-direct to `env`'s public host via
 * `crossEnvClient`. */
export async function fetchEnvSnapshot(env: string): Promise<EnvSnapshot> {
  const client = crossEnvClient(env);

  const [permissionsRes, rolesRes, usersRes, groupsRes, tenantsRes, customersRes, sponsorsRes] = await Promise.all([
    client.get<RawPermission[]>('/api/admin/rbac-v2/permissions'),
    client.get<RawRole[]>('/api/admin/rbac-v2/roles'),
    client.get<{ rows: RawAdminUser[] }>('/api/admin/users'),
    client.get<RawGroup[]>('/api/admin/rbac-v2/tenant-groups'),
    client.get<{ tenant_code: string }[]>('/api/tenant'),
    client.get<{ customer_code: string }[]>('/api/lookup/customers'),
    client.get<{ sponsor_code: string }[]>('/api/lookup/sponsors'),
  ]);

  const rawUsers = usersRes.data.rows;
  const groupNamesByUser = await mapWithConcurrency(rawUsers, GROUP_FETCH_CONCURRENCY, async (u) => {
    const { data } = await client.get<{ name: string }[]>(`/api/admin/rbac-v2/users/${u.user_id}/groups`);
    return data.map((g) => g.name);
  });

  const roles: RoleSnapshot[] = rolesRes.data.map((r) => ({
    role_code: r.role_code,
    role_name: r.role_name,
    description: r.description,
    permissions: (r.permissions ?? []) as PermissionCode[],
    is_system: r.is_system,
  }));

  const users: UserSnapshot[] = rawUsers.map((u, i) => ({
    id: u.user_id,
    email: u.email,
    display_name: u.display_name,
    active: u.active,
    role_v2_code: u.role_v2_code ?? 'default',
    groups: groupNamesByUser[i] ?? [],
  }));

  const groups: GroupSnapshot[] = groupsRes.data.map((g) => ({
    id: g.id,
    name: g.name,
    level: g.level,
    members: g.members ?? [],
  }));

  return {
    env,
    permissionCatalog: new Set(permissionsRes.data.map((p) => p.permission_code as PermissionCode)),
    roles,
    users,
    groups,
    scopeUniverse: {
      tenant: new Set(tenantsRes.data.map((t) => t.tenant_code)),
      customer: new Set(customersRes.data.map((c) => c.customer_code)),
      sponsor: new Set(sponsorsRes.data.map((s) => s.sponsor_code)),
    },
  };
}
